import { useState, useEffect } from 'react';
import { fetchPackageSummaryById } from '@/api/reports';
import type { PackageSummaryResponse } from '@/types/packageSummary';

interface UsePackageSummaryResult {
  summary: PackageSummaryResponse | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook to fetch package summary by report ID
 */
export const usePackageSummary = (reportId: string | null): UsePackageSummaryResult => {
  const [summary, setSummary] = useState<PackageSummaryResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!reportId) {
      return;
    }

    const loadSummary = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const data = await fetchPackageSummaryById(reportId);
        setSummary(data);
      } catch (err) {
        console.error('Failed to fetch package summary:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch package summary');
      } finally {
        setIsLoading(false);
      }
    };

    loadSummary();
  }, [reportId]);

  return { summary, isLoading, error };
};
